import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Upload,
  Download,
  ArrowLeft,
  CheckCircle2,
  Pencil,
  CheckCircle,
  HelpCircle,
  AlertCircle,
  Loader2,
  Plus,
  Info,
} from "lucide-react";
import {
  useValidateAgreementImport,
  useValidateAgreementRows,
  useCommitAgreementRows,
  useProductOptions,
} from "../hooks";
import { downloadAgreementTemplate } from "../api";
import { GREEN, AGREEMENT_TYPES, previewRowToInput } from "../constants";
import { Toast } from "../components/Toast";
import { Stat } from "../components/Stat";
import { StatusBadge } from "../components/StatusBadge";
import { EditCell } from "../components/EditCell";
import { ProductSelect } from "../components/ProductSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SELECT_NONE,
} from "@/shared/ui/Select";
import { Dropzone } from "../components/Dropzone";
import { AgreementEditDrawer } from "../drawers/AgreementEditDrawer";
import { AgreementPreviewDetailDrawer } from "../drawers/AgreementPreviewDetailDrawer";
import { InlineProductAddModal } from "../InlineProductAddModal";
import type {
  Merchant,
  AgreementImportPreview,
  AgreementImportResult,
  AgreementCommitCreatedItem,
  AgreementPreviewRow,
  AgreementRowInput,
  ProductOption,
} from "../types";

// Step 3 (agreements mode) — upload an XLSX of agreements, edit/re-validate the preview rows, commit,
// then fill per-loan detail fields. The editable table is the source of truth after the first upload.
export function AgreementStep({
  merchant,
  onBack,
  onReset,
  onResultChange,
}: {
  merchant: Merchant;
  onBack: () => void;
  onReset: () => void;
  // Lifts the "has a committed result" flag so the wizard stepper can advance to the result step.
  onResultChange?: (hasResult: boolean) => void;
}) {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<AgreementImportPreview | null>(null);
  // Editable raw copy of the preview rows — the source of truth for the editable table.
  const [editRows, setEditRows] = useState<AgreementRowInput[] | null>(null);
  // True while a debounced re-validate is pending or in flight (blocks commit, shows "checking…").
  const [isRevalidating, setIsRevalidating] = useState(false);
  // Bumped on every cell edit so the debounce effect re-runs only on genuine user edits (not on seed).
  const [editVersion, setEditVersion] = useState(0);
  const [result, setResult] = useState<AgreementImportResult | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Track local missing counts so the list updates after each drawer save.
  const [missingCounts, setMissingCounts] = useState<Record<number, number>>({});
  // Which loan_id's drawer is currently open (null = closed).
  const [editingLoanId, setEditingLoanId] = useState<number | null>(null);
  // Which preview row index opened the inline catalogue-add modal (null = closed). Feature C.
  const [catalogueRowIndex, setCatalogueRowIndex] = useState<number | null>(null);
  // Which preview row index opened the pre-commit detail drawer (null = closed).
  const [detailRowIndex, setDetailRowIndex] = useState<number | null>(null);

  const { mutate: validate, isPending: isValidating } = useValidateAgreementImport();
  const { mutate: validateRows } = useValidateAgreementRows();
  const { mutate: commitRows, isPending: isCommitting } = useCommitAgreementRows();

  // Keep the wizard stepper in sync with whether a result screen is showing.
  useEffect(() => {
    onResultChange?.(result !== null);
  }, [result, onResultChange]);

  const handleFile = useCallback(
    (file: File) => {
      setSelectedFile(file);
      setPreview(null);
      setEditRows(null);
      setEditVersion(0);
      validate(
        { merchantId: merchant.id, file },
        {
          onSuccess: (data) => {
            setPreview(data);
            // Seed the editable rows from the file-based preview. From here on the table is
            // driven by editRows; commit re-sends these as JSON (no second file upload).
            setEditRows(data.rows.map(previewRowToInput));
          },
        },
      );
    },
    [merchant.id, validate],
  );

  // Clear the chosen file and its derived preview/edit state, back to the empty dropzone.
  const handleClearFile = useCallback(() => {
    setSelectedFile(null);
    setPreview(null);
    setEditRows(null);
    setEditVersion(0);
  }, []);

  // Update a single editable cell and flag that a re-validate is needed.
  const updateCell = useCallback(
    (index: number, field: keyof AgreementRowInput, value: string) => {
      setEditRows((prev) =>
        prev ? prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)) : prev,
      );
      setIsRevalidating(true); // reflect intent immediately, before the debounce settles
      setEditVersion((v) => v + 1);
    },
    [],
  );

  // Write the pre-commit detail overrides for one row (from AgreementPreviewDetailDrawer) and
  // trigger a re-validate so the row's missing-fields hint refreshes. additional_data rides the
  // editRows payload to the backend at commit.
  const updateRowAdditionalData = useCallback(
    (index: number, data: Record<string, string>) => {
      setEditRows((prev) =>
        prev ? prev.map((r, i) => (i === index ? { ...r, additional_data: data } : r)) : prev,
      );
      setIsRevalidating(true);
      setEditVersion((v) => v + 1);
    },
    [],
  );

  // Debounced auto re-validate: ~600ms after the last edit, send editRows as JSON {rows}.
  // Each edit resets the timer (clearTimeout), so only the final state is validated.
  useEffect(() => {
    if (editVersion === 0 || !editRows) return;
    const handle = setTimeout(() => {
      validateRows(
        { merchantId: merchant.id, rows: editRows },
        {
          onSuccess: (data) => {
            setPreview(data);
            setIsRevalidating(false);
          },
          onError: () => setIsRevalidating(false),
        },
      );
    }, 600);
    return () => clearTimeout(handle);
  }, [editVersion, editRows, merchant.id, validateRows]);

  const handleCommit = useCallback(() => {
    if (!editRows) return;
    commitRows(
      { merchantId: merchant.id, rows: editRows },
      {
        onSuccess: (data) => {
          setResult(data);
          setToast(t("import.agreements.importedToast", { count: data.imported_count }));
        },
      },
    );
  }, [merchant.id, editRows, commitRows, t]);

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      await downloadAgreementTemplate();
    } finally {
      setIsDownloading(false);
    }
  }, []);

  // Callback from drawer: update the local missing-count cache for the given loan.
  const handleDetailSaved = useCallback((loanId: number, missingCount: number) => {
    setMissingCounts((prev) => ({ ...prev, [loanId]: missingCount }));
  }, []);

  // Catalogue product options for car/technology rows in the preview table.
  // Both hooks are always called (Rules of Hooks); enabled only when relevant rows exist.
  const hasCar = editRows?.some((r) => r.type === "car") ?? false;
  const hasTech = editRows?.some((r) => r.type === "technology") ?? false;
  const { data: carOptions = [], isLoading: carOptionsLoading } = useProductOptions(
    "car",
    hasCar,
  );
  const { data: techOptions = [], isLoading: techOptionsLoading } = useProductOptions(
    "technology",
    hasTech,
  );

  // Write a catalogue product selection into a preview row's additional_data + name field.
  // Mirrors AgreementEditDrawer.handleProductSelect exactly (same field names).
  const handleRowProductSelect = useCallback(
    (index: number, type: string, opt: ProductOption) => {
      // Update the display name so the name cell reflects the canonical catalogue name.
      updateCell(index, "name", opt.name);
      // Write model id + canonical name into additional_data so the commit payload is correct.
      const adKey = type === "car" ? "model" : "technology_model";
      const adIdKey = type === "car" ? "model_type_id" : "technology_model_id";
      setEditRows((prev) =>
        prev
          ? prev.map((r, i) =>
              i === index
                ? {
                    ...r,
                    name: opt.name,
                    additional_data: {
                      ...r.additional_data,
                      [adKey]: opt.name,
                      [adIdKey]: String(opt.id),
                    },
                  }
                : r,
            )
          : prev,
      );
      setIsRevalidating(true);
      setEditVersion((v) => v + 1);
    },
    [updateCell],
  );

  // Result screen — aggregate stats + per-created-agreement list with Edit buttons.
  if (result) {
    return (
      <div className="mx-auto max-w-2xl">
        {/* Aggregate stats card */}
        <div className="mb-6 rounded-card border border-grey-200 bg-white p-5 text-center sm:p-8">
          <CheckCircle2 size={48} color={GREEN} strokeWidth={1.5} className="mx-auto mb-4" />
          <h3 className="mb-4 text-[18px] font-semibold text-grey-800">
            {t("import.agreements.resultTitle")}
          </h3>
          <div className="mb-6 grid grid-cols-3 gap-3 text-center">
            <Stat label={t("import.agreements.imported")} value={result.imported_count} color="green" />
            <Stat label={t("import.agreements.skipped")} value={result.skipped_exists} color="amber" />
            <Stat label={t("import.agreements.errors")} value={result.error_count} color="red" />
          </div>
          <button
            type="button"
            onClick={onReset}
            className="rounded-control bg-green px-6 py-2.5 text-[14px] font-semibold text-white transition hover:bg-green-dark active:scale-95"
          >
            {t("import.agreements.newImport")}
          </button>
        </div>

        {/* Per-agreement list with Edit buttons */}
        {result.created.length > 0 && (
          <div className="rounded-card border border-grey-200 bg-white">
            <ul className="divide-y divide-grey-100">
              {result.created.map((item: AgreementCommitCreatedItem) => {
                // Use local missing count if available (updated after drawer save).
                const missing =
                  item.loan_id in missingCounts
                    ? missingCounts[item.loan_id]
                    : item.missing_detail_fields.length;
                return (
                  <li
                    key={item.loan_id}
                    className="flex items-center justify-between gap-4 px-4 py-4 sm:px-5"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-[14px] font-semibold text-grey-800">
                          {item.agreement_code}
                        </span>
                        <span className="rounded-pill bg-grey-100 px-2 py-0.5 text-[11px] text-grey-600">
                          {t(`import.agreements.loanType.${item.type}`)}
                        </span>
                        {missing > 0 && (
                          <span className="rounded-pill bg-warning-bg px-2 py-0.5 text-[11px] font-semibold text-warning">
                            {t("import.agreements.edit.missingHint", { count: missing })}
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 truncate text-[13px] text-grey-500">{item.client_label}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditingLoanId(item.loan_id)}
                      className="flex shrink-0 items-center gap-1.5 rounded-control border border-grey-200 px-3.5 py-2 text-[13px] font-medium text-grey-700 transition-colors hover:bg-grey-50"
                    >
                      <Pencil size={14} strokeWidth={2} />
                      {t("import.agreements.edit.editBtn")}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {toast && <Toast message={toast} onClose={() => setToast(null)} />}

        {/* Edit drawer — mounted when a row's Edit button is clicked */}
        {editingLoanId !== null && (
          <AgreementEditDrawer
            loanId={editingLoanId}
            onClose={() => setEditingLoanId(null)}
            onSaved={handleDetailSaved}
          />
        )}
      </div>
    );
  }

  return (
    // Full-width on large screens — the preview table has many columns and needs the room.
    <div className="w-full">
      <button
        type="button"
        onClick={onBack}
        className="mb-4 flex items-center gap-1.5 text-[13px] text-grey-500 hover:text-grey-700"
      >
        <ArrowLeft size={14} strokeWidth={2} />
        {t("import.back")}
      </button>

      <div className="mb-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-[18px] font-semibold text-grey-800">{t("import.agreements.title")}</h2>
          <p className="text-[13px] text-grey-500">
            {merchant.first_name} {merchant.last_name}
          </p>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={isDownloading}
          className="flex items-center gap-2 rounded-control border border-grey-200 bg-white px-4 py-2 text-[13px] font-medium text-grey-700 transition-colors hover:bg-grey-50 disabled:opacity-60"
        >
          <Download size={15} strokeWidth={1.8} />
          {isDownloading ? t("app.loading") : t("import.agreements.downloadTemplate")}
        </button>
      </div>

      {/* File dropzone — shared drag/drop + file-type guard + selected-file chip */}
      <Dropzone
        file={selectedFile}
        onFile={handleFile}
        onClear={handleClearFile}
        disabled={isValidating || isCommitting}
      />

      {/* Validation skeleton */}
      {isValidating && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-card bg-grey-100" />
            ))}
          </div>
          <div className="h-64 animate-pulse rounded-card bg-grey-100" />
        </div>
      )}

      {/* Preview table */}
      {preview && !isValidating && (
        <>
          {/* Summary counts */}
          <div className="mb-4 grid grid-cols-3 gap-3">
            <Stat label={t("import.agreements.readyCount")} value={preview.ready_count} color="green" />
            <Stat label={t("import.agreements.existsCount")} value={preview.exists_count} color="amber" />
            <Stat label={t("import.agreements.errorCount")} value={preview.error_count} color="red" />
          </div>

          {/* Editable-table hint + live re-validation indicator. */}
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="text-[12px] text-grey-500">{t("import.agreements.editHint")}</p>
            {isRevalidating && (
              <span className="inline-flex shrink-0 items-center gap-1.5 text-[12px] text-grey-500">
                <Loader2 size={13} className="animate-spin" />
                {t("import.agreements.revalidating")}
              </span>
            )}
          </div>

          {/* Legend — explains the model-match badges in the "Mahsulot/Model" column. */}
          <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-grey-500">
            <span className="font-medium text-grey-600">
              {t("import.agreements.match.legendTitle")}
            </span>
            <span className="inline-flex items-center gap-1">
              <CheckCircle size={13} className="text-green-dark" />
              {t("import.agreements.match.matched")}
            </span>
            <span className="inline-flex items-center gap-1">
              <HelpCircle size={13} className="text-warning" />
              {t("import.agreements.match.suggested")}
            </span>
            <span className="inline-flex items-center gap-1">
              <AlertCircle size={13} className="text-grey-400" />
              {t("import.agreements.match.unmatched")}
            </span>
          </div>

          {/* Row table — the row# and client columns are frozen to the left so context survives the
              horizontal scroll across the many editable columns. */}
          <div className="mb-5 overflow-x-auto rounded-card border border-grey-200">
            <table className="min-w-full border-collapse text-[13px]">
              {/* Sticky header — remains visible while scrolling down through many rows. */}
              <thead className="sticky top-0 z-30 bg-white shadow-[0_1px_0_0_#e5e7eb]">
                <tr>
                  {/* Numeric columns are right-aligned in the header to match their cell alignment. */}
                  {(
                    [
                      { key: "colRow", numeric: false },
                      { key: "colClient", numeric: false },
                      { key: "colIdentifier", numeric: false },
                      { key: "colType", numeric: false },
                      { key: "colName", numeric: false },
                      { key: "colCost", numeric: true },
                      { key: "colSellingPrice", numeric: true },
                      { key: "colDownPayment", numeric: true },
                      { key: "colMonthlyPayment", numeric: true },
                      { key: "colPaymentCount", numeric: true },
                      { key: "colStartDate", numeric: false },
                      { key: "colPaymentDay", numeric: true },
                      { key: "colPaidAmount", numeric: true },
                      { key: "colNote", numeric: false },
                      { key: "colReminder", numeric: true },
                      { key: "colStatus", numeric: false },
                      { key: "colMessage", numeric: false },
                    ] as { key: string; numeric: boolean }[]
                  ).map(({ key, numeric }) => {
                    const sticky =
                      key === "colRow"
                        ? "sticky left-0 z-20 w-[48px] min-w-[48px] bg-grey-50"
                        : key === "colClient"
                          ? "sticky left-[48px] z-20 border-r border-grey-200 bg-grey-50"
                          : "";
                    return (
                      <th
                        key={key}
                        className={`whitespace-nowrap px-3 py-2.5 font-semibold text-grey-500 ${numeric ? "text-right" : "text-left"} ${sticky}`}
                      >
                        {t(`import.agreements.${key}`)}
                      </th>
                    );
                  })}
                  {/* Pre-commit detail drawer trigger column */}
                  <th className="whitespace-nowrap px-3 py-2.5 text-center font-semibold text-grey-500">
                    {t("import.agreements.detailCol")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-grey-100 bg-white">
                {editRows?.map((input, index) => {
                  // Validation metadata (status colour, message, match badge, resolved client) comes
                  // from the aligned preview row; editable values come from editRows. They stay in
                  // lockstep because re-validate never reorders or changes the row count.
                  const row = preview.rows[index] as AgreementPreviewRow | undefined;
                  const status = row?.status;
                  const bg =
                    status === "ready"
                      ? "bg-green-bg"
                      : status === "exists"
                        ? "bg-warning-bg"
                        : status === "error"
                          ? "bg-red-bg"
                          : "bg-white";

                  // Determine if this row uses catalogue product selection (car / technology).
                  const isCatalogueType = input.type === "car" || input.type === "technology";
                  const productOpts = input.type === "car" ? carOptions : techOptions;
                  const productOptsLoading =
                    input.type === "car" ? carOptionsLoading : techOptionsLoading;

                  // Resolve the current model name for the ProductSelect value.
                  // Priority: already-written additional_data model key → match canonical → raw name.
                  const adModelKey = input.type === "car" ? "model" : "technology_model";
                  const currentProductName =
                    input.additional_data?.[adModelKey] ??
                    (row?.match?.status === "matched" || row?.match?.status === "suggested"
                      ? (row.match.canonical ?? input.name)
                      : input.name);

                  return (
                    <tr
                      key={index}
                      className={`transition-colors hover:brightness-[0.98] [&>td]:align-top ${bg}`}
                    >
                      {/* Frozen row-number cell */}
                      <td
                        className={`sticky left-0 z-10 w-[48px] min-w-[48px] px-3 py-2 text-right tnum text-grey-400 ${bg}`}
                      >
                        {row?.row ?? index + 1}
                      </td>
                      {/* Frozen client name cell */}
                      <td
                        className={`sticky left-[48px] z-10 whitespace-nowrap border-r border-grey-200 px-3 py-2 text-grey-800 ${bg}`}
                      >
                        {row?.client ? row.client.label : "—"}
                      </td>
                      <td className="px-2 py-1.5">
                        <EditCell
                          value={input.identifier}
                          onChange={(v) => updateCell(index, "identifier", v)}
                          className="w-32"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Select
                          value={input.type === "" ? SELECT_NONE : input.type}
                          onValueChange={(v) =>
                            updateCell(index, "type", v === SELECT_NONE ? "" : v)
                          }
                        >
                          <SelectTrigger dense className="w-28">
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={SELECT_NONE}>—</SelectItem>
                            {AGREEMENT_TYPES.map((k) => (
                              <SelectItem key={k} value={k}>
                                {t(`import.agreements.loanType.${k}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      {/* Name / model column — ProductSelect for catalogue types, free-text EditCell otherwise */}
                      <td className="px-2 py-1.5">
                        {isCatalogueType ? (
                          <div className="w-52">
                            <ProductSelect
                              options={productOpts}
                              value={currentProductName}
                              onChange={(opt) => handleRowProductSelect(index, input.type, opt)}
                              placeholder={t("import.agreements.edit.modelPlaceholder")}
                              searchPlaceholder={t("import.agreements.edit.modelSearch")}
                              isLoading={productOptsLoading}
                              dense
                            />
                            {/* Match badge below the select so inputs align on the same row baseline */}
                            {row?.match && (
                              <div className="mt-1 flex items-center gap-1 text-[11px]">
                                {row.match.status === "matched" && (
                                  <span className="inline-flex items-center gap-0.5 rounded-full bg-green-bg px-1.5 py-0.5 text-green-dark">
                                    <CheckCircle size={11} className="shrink-0" />
                                    {t("import.agreements.match.matched")}
                                  </span>
                                )}
                                {row.match.status === "suggested" && (
                                  <span className="inline-flex items-center gap-0.5 rounded-full bg-warning-bg px-1.5 py-0.5 text-warning">
                                    <HelpCircle size={11} className="shrink-0" />
                                    {t("import.agreements.match.suggested")}
                                  </span>
                                )}
                                {row.match.status === "unmatched" && (
                                  <span className="inline-flex items-center gap-0.5 rounded-full bg-grey-100 px-1.5 py-0.5 text-grey-500">
                                    <AlertCircle size={11} className="shrink-0" />
                                    {t("import.agreements.match.unmatched")}
                                  </span>
                                )}
                              </div>
                            )}
                            {/* Inline catalogue-add (Feature C) — only for unmatched/suggested rows */}
                            {row?.match &&
                              (row.match.status === "unmatched" ||
                                row.match.status === "suggested") && (
                                <button
                                  type="button"
                                  onClick={() => setCatalogueRowIndex(index)}
                                  className="mt-1 inline-flex items-center gap-1 text-[12px] font-medium text-green hover:underline"
                                >
                                  <Plus size={12} className="shrink-0" />
                                  {t("import.agreements.catalogue.addToCatalogue")}
                                </button>
                              )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <EditCell
                              value={input.name}
                              onChange={(v) => updateCell(index, "name", v)}
                              className="w-40"
                            />
                            {/* Model-match badge for non-catalogue types */}
                            {row?.match &&
                              (() => {
                                const { status: mStatus, suggestion } = row.match!;
                                if (mStatus === "matched") {
                                  return (
                                    <span title={t("import.agreements.match.matched")}>
                                      <CheckCircle size={14} className="shrink-0 text-green-dark" />
                                    </span>
                                  );
                                }
                                if (mStatus === "suggested") {
                                  return (
                                    <span
                                      className="inline-flex items-center gap-0.5 text-warning"
                                      title={t("import.agreements.match.didYouMean", { suggestion })}
                                    >
                                      <HelpCircle size={14} className="shrink-0 text-warning" />
                                      <span className="whitespace-nowrap text-[12px]">
                                        → {suggestion}
                                      </span>
                                    </span>
                                  );
                                }
                                return (
                                  <span title={t("import.agreements.match.unmatched")}>
                                    <AlertCircle size={14} className="shrink-0 text-grey-400" />
                                  </span>
                                );
                              })()}
                          </div>
                        )}
                      </td>
                      {/* Numeric cells — right-aligned with tabular-nums for column readability */}
                      <td className="px-2 py-1.5 text-right">
                        <EditCell
                          value={input.cost_price}
                          onChange={(v) => updateCell(index, "cost_price", v)}
                          kind="numeric"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <EditCell
                          value={input.selling_price}
                          onChange={(v) => updateCell(index, "selling_price", v)}
                          kind="numeric"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <EditCell
                          value={input.down_payment}
                          onChange={(v) => updateCell(index, "down_payment", v)}
                          kind="numeric"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <EditCell
                          value={input.monthly_payment}
                          onChange={(v) => updateCell(index, "monthly_payment", v)}
                          kind="numeric"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <EditCell
                          value={input.payment_count}
                          onChange={(v) => updateCell(index, "payment_count", v)}
                          kind="numeric"
                          className="w-20"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <EditCell
                          value={input.first_payment_date}
                          onChange={(v) => updateCell(index, "first_payment_date", v)}
                          kind="date"
                          className="w-36"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <EditCell
                          value={input.payment_day}
                          onChange={(v) => updateCell(index, "payment_day", v)}
                          kind="numeric"
                          className="w-16"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <EditCell
                          value={input.paid_amount}
                          onChange={(v) => updateCell(index, "paid_amount", v)}
                          kind="numeric"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <EditCell
                          value={input.note}
                          onChange={(v) => updateCell(index, "note", v)}
                          className="w-32"
                        />
                      </td>
                      {/* Reminder days — optional numeric field (null/blank = no reminder) */}
                      <td className="px-2 py-1.5 text-right">
                        <EditCell
                          value={input.notification_before}
                          onChange={(v) => updateCell(index, "notification_before", v)}
                          kind="numeric"
                          className="w-20"
                        />
                      </td>
                      <td className="px-3 py-2">
                        {row ? <StatusBadge status={row.status} t={t} /> : null}
                      </td>
                      <td className="min-w-[160px] px-3 py-2 text-[12px]">
                        {row?.status === "exists" && row.existing && (
                          <span className="text-warning">
                            {t("import.agreements.existsHint", { code: row.existing.agreement_code })}
                          </span>
                        )}
                        {row?.status === "error" && row.message && (
                          <span className="text-red">{row.message}</span>
                        )}
                        {row?.status === "ready" &&
                          row.missing_detail_fields &&
                          row.missing_detail_fields.length > 0 && (
                            <span className="text-grey-400">
                              {t("import.agreements.missingFields", {
                                fields: row.missing_detail_fields.join(", "),
                              })}
                            </span>
                          )}
                      </td>
                      {/* Pre-commit "full detail" drawer trigger — ready rows only */}
                      <td className="px-2 py-2 text-center">
                        {row?.status === "ready" && (
                          <button
                            type="button"
                            onClick={() => setDetailRowIndex(index)}
                            title={t("import.agreements.detailBtnTitle")}
                            aria-label={t("import.agreements.detailBtnTitle")}
                            className="text-grey-400 hover:text-green"
                          >
                            <Info size={16} strokeWidth={2} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onBack}
              className="rounded-control border border-grey-200 px-5 py-2.5 text-[14px] font-medium text-grey-700 hover:bg-grey-50"
            >
              {t("import.back")}
            </button>
            <button
              type="button"
              onClick={handleCommit}
              disabled={isCommitting || isRevalidating || preview.ready_count === 0}
              className="flex items-center gap-2 rounded-control bg-green px-5 py-2.5 text-[14px] font-semibold text-white transition hover:bg-green-dark active:scale-95 disabled:opacity-60 disabled:active:scale-100"
            >
              {isCommitting ? (
                <Loader2 size={16} strokeWidth={2} className="animate-spin" />
              ) : (
                <Upload size={16} strokeWidth={2} />
              )}
              {isCommitting
                ? t("app.loading")
                : t("import.agreements.commitBtn", { count: preview.ready_count })}
            </button>
          </div>
        </>
      )}

      {/* Inline catalogue-add modal (Feature C) — opened from an unmatched/suggested row's name cell.
          The type guard mirrors the trigger condition, so the cast to car|technology is always safe. */}
      {catalogueRowIndex !== null &&
        (editRows?.[catalogueRowIndex]?.type === "car" ||
          editRows?.[catalogueRowIndex]?.type === "technology") && (
          <InlineProductAddModal
            open
            loanType={editRows[catalogueRowIndex].type as "car" | "technology"}
            initialName={editRows[catalogueRowIndex].name}
            onClose={() => setCatalogueRowIndex(null)}
            onPicked={(canonical) => updateCell(catalogueRowIndex, "name", canonical)}
          />
        )}

      {/* Pre-commit detail drawer — fills whitelisted detail fields before the agreement is created.
          preview.rows and editRows share index order (re-validate returns the full list). */}
      {detailRowIndex !== null &&
        editRows?.[detailRowIndex] &&
        preview?.rows?.[detailRowIndex] && (
          <AgreementPreviewDetailDrawer
            row={preview.rows[detailRowIndex]}
            input={editRows[detailRowIndex]}
            onSave={(data) => updateRowAdditionalData(detailRowIndex, data)}
            onClose={() => setDetailRowIndex(null)}
          />
        )}

      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
